let currentLocale = "en-IN";
let currentCurrency = "₹";

export const updateFormatContext = (settings) => {
  if (settings?.locale) currentLocale = settings.locale;
  if (settings?.currency_symbol) currentCurrency = settings.currency_symbol;
};

/**
 * Shared display formatting for money and elapsed time.
 */

/**
 * Formats money according to the resolved locale and currency symbol.
 *
 * Always two decimal places, including on whole rupees.
 *
 * `maximumFractionDigits` alone printed ₹700 beside ₹699.99 in the same column,
 * so the decimal point moved from row to row and a cashier scanning a bill for
 * the odd figure had to read every character of every line instead of running
 * an eye down one vertical rule. Two fixed decimals put the point in a fixed
 * place; the tabular-nums in the money slots below keep the digits in fixed
 * places too. Together they are what makes a paise column a column.
 *
 * Values stay in rupees, undivided — this never sees paise (project rule: a
 * paise value here renders a ₹325 item as ₹32,500.00).
 */
export const inr = value => {
  // If the currency symbol is already a prefix (like ₹ or $), prepend it.
  // If we wanted to use Intl.NumberFormat with 'currency', we would need the actual currency code (INR, USD).
  // For POS flexibility, we manually prepend the custom symbol from settings.
  //
  // The guard has to come AFTER coercion. `Number(value || 0)` reads as a NaN
  // guard and is not one: `|| 0` tests the raw value, and any non-numeric
  // string is truthy, so it passed straight through to Number() and printed
  // ₹NaN on a payment screen. Only `undefined`/`null`/`""`/`NaN` were ever
  // caught. isFinite also stops ₹∞ from a divide-by-zero upstream.
  const numeric = Number(value);
  const formatted = (Number.isFinite(numeric) ? numeric : 0).toLocaleString(currentLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currentCurrency}${formatted}`;
};

/**
 * The three type slots money is allowed to occupy.
 *
 * Money was being sized ad hoc — `text-sm`, `text-[11px]`, `text-3xl`, plus a
 * `.toFixed(2)` formatter in BillHistoryPanel and another in ReceiptPreview and
 * a third in SplitForm — and `tabular-nums` appeared at six call sites in the
 * whole product. Proportional digits mean 1 is narrower than 8, so a column of
 * totals fans out and the eye cannot compare magnitudes by length. Every slot
 * here carries tabular-nums; that is the point of them existing.
 *
 * Type only. No colour and no alignment: colour is the call site's (a negative
 * balance is not a different size), and alignment is the container's — these
 * slots appear inside sentences as often as inside columns.
 *
 * MONEY_GRAND_TOTAL — the one figure that settles the transaction. One per
 *   screen: bill grand total, amount due, shift expected cash.
 * MONEY_LINE — every figure in a list or table: line totals, ledger rows,
 *   payment allocations, running balances.
 * MONEY_CAPTION — money that qualifies another figure rather than stating one:
 *   "saves ₹25.00", unit prices under an item name, tax component breakdowns.
 */
export const MONEY_GRAND_TOTAL = "text-display font-bold tabular-nums";
export const MONEY_LINE = "text-body font-medium tabular-nums";
export const MONEY_CAPTION = "text-meta tabular-nums";

/**
 * A duration in minutes as "45 min", "3h 20m", "2d 1h".
 */
export const elapsedFromMinutes = minutes => {
  const total = Math.max(0, Math.floor(Number(minutes) || 0));
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  if (hours < 24) return `${hours}h ${total % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

export const elapsedFromSeconds = seconds =>
  elapsedFromMinutes(Math.floor((Number(seconds) || 0) / 60));
